import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../shared/providers/LanguageProvider';
import merchantService from '../../services/merchantService';
import { ShimmerBox, ShimmerCard } from '../../components/common/Shimmer';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const MerchantProfile = () => {
  const { userProfile, session } = useAuth();
  const { t, isRTL } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [formData, setFormData] = useState({
    business_name: '',
    email: '',
    phone: '',
    address: '',
    description: '',
    image_gallery: []
  });

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.token, session?.access_token]);

  useEffect(() => {
    if (userProfile?.token || session?.access_token) {
      const token = userProfile?.token || session?.access_token;
      merchantService.setAuthToken(token);
    }
  }, [userProfile?.token, session?.access_token]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError(t('noAuthenticationTokenAvailable'));
        return;
      }

      merchantService.setAuthToken(token);
      const response = await merchantService.getProfile();
      
      if (response.success && response.data) {
        setProfile(response.data);
        setFormData({
          business_name: response.data.business_name || '',
          email: response.data.email || response.data.user?.email || '',
          phone: response.data.phone || '',
          address: response.data.address || '',
          description: response.data.description || '',
          image_gallery: response.data.image_gallery || []
        });
      } else {
        setError(response.error || t('failedToFetchProfile'));
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError(error.response?.data?.error || error.message || t('failedToFetchProfile'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError(t('noAuthenticationTokenAvailable'));
        return;
      }

      merchantService.setAuthToken(token);
      const response = await merchantService.updateProfile(formData);
      
      if (response.success) {
        setProfile(response.data);
        setSuccess(t('profileUpdatedSuccessfully'));
        setEditing(false);
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(response.error || t('failedToUpdateProfile'));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.details?.[0]?.msg || error.message || t('failedToUpdateProfile');
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('nA');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <ShimmerBox height="h-12" />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <ShimmerCard />
          <ShimmerCard />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <Card className="border-destructive/50 bg-destructive/10">
        <CardContent className="flex items-center gap-4 pt-6 text-destructive">
          <span>{error || t('profileNotFound')}</span>
          <Button variant="link" onClick={fetchProfile}>{t('retry')}</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('merchantProfile')}
        description={t('manageYourBusinessProfile')}
        actions={
          !editing ? (
            <Button onClick={() => setEditing(true)}>{t('edit')}</Button>
          ) : null
        }
      />

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6 text-destructive">{error}</CardContent>
        </Card>
      )}

      {success && (
        <Card className="border-emerald-500/50 bg-emerald-500/10">
          <CardContent className="pt-6 text-emerald-700 dark:text-emerald-400">{success}</CardContent>
        </Card>
      )}

      {profile.cover_image_url && (
        <div className="relative h-64 overflow-hidden rounded-xl border">
          <img
            src={profile.cover_image_url}
            alt={profile.business_name || t('coverImage')}
            className="h-full w-full object-cover"
          />
        </div>
      )}

      {editing ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('businessInformation')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="business_name">{t('businessName')}</Label>
                  <Input
                    id="business_name"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('phoneNumber')}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">{t('address')}</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t('description')}</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                />
              </div>

              <div className="flex gap-4">
                <Button type="submit" disabled={saving}>
                  {saving ? t('saving') : t('save')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    setFormData({
                      business_name: profile.business_name || '',
                      email: profile.email || profile.user?.email || '',
                      phone: profile.phone || '',
                      address: profile.address || '',
                      description: profile.description || '',
                      image_gallery: profile.image_gallery || []
                    });
                    setError(null);
                  }}
                >
                  {t('cancel')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>{t('businessInformation')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">{t('businessName')}</Label>
                <p className="font-medium text-foreground">{profile.business_name || t('nA')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('legalName')}</Label>
                <p className="text-foreground">{profile.legal_name || t('nA')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('contactPerson')}</Label>
                <p className="text-foreground">{profile.contact_person || t('nA')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('phoneNumber')}</Label>
                <p className="text-foreground">{profile.phone || t('nA')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('email')}</Label>
                <p className="text-foreground">{profile.email || profile.user?.email || t('nA')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('address')}</Label>
                <p className="text-foreground">{profile.address || t('nA')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('city')}</Label>
                <p className="text-foreground">{profile.city || t('nA')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('businessCategory')}</Label>
                <p className="text-foreground">{profile.business_category || t('nA')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('status')}</Label>
                <div className="mt-1">
                  <Badge variant={profile.status === 'active' ? 'success' : 'destructive'}>
                    {profile.status}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('accountInformation')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">{t('username')}</Label>
                <p className="font-medium text-foreground">{profile.user?.username || t('nA')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('email')}</Label>
                <p className="text-foreground">{profile.user?.email || t('nA')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('role')}</Label>
                <p className="text-foreground">{profile.user?.role || t('nA')}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('bankInformation')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">{t('bankAccountIBAN')}</Label>
                <p className="text-foreground">{profile.bank_account_iban || t('nA')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('bankAccountHolder')}</Label>
                <p className="text-foreground">{profile.bank_account_holder || t('nA')}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('additionalInformation')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">{t('crNumber')}</Label>
                <p className="text-foreground">{profile.cr_number || t('nA')}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('createdAt')}</Label>
                <p className="text-foreground">{formatDate(profile.created_at)}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('updatedAt')}</Label>
                <p className="text-foreground">{formatDate(profile.updated_at)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {profile.image_gallery && profile.image_gallery.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('imageGallery')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {profile.image_gallery.map((image, index) => (
                <div key={index} className="relative">
                  <img
                    src={image.url}
                    alt={image.alt || `${t('image')} ${index + 1}`}
                    className="h-32 w-full rounded-lg object-cover"
                  />
                  {image.is_primary && (
                    <Badge className="absolute start-2 top-2">{t('primary')}</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MerchantProfile;
